import * as React from 'react';
import { useEffect, useRef, useState, useCallback } from 'react';
import SvgSolveButton from '../../svg/SvgSolveButton';
import SvgSolveButtonSurround from '../../svg/SolveButtonSurround';
import PrismSlot, { PrismFace } from '../PrismSlot';
import { createPrismFromSlot } from '../../utils/prismGeometry';
import SvgBoardCloseButton from '../../svg/BoardCloseButton';

// Prism geometry — 718×144px slot, face = slot
const SOLVE = createPrismFromSlot('horiz', 144, 718);

const SOLVE_PRISM_DURATION = 2520;
const SOLVE_OUTCOME_REVEAL_PAUSE = 100;
import { COLORS } from '../../utils/constants';
const HIGHLIGHT = COLORS.highlight;
const CREAM = '#f7e5c5';
const OUTCOME_GREEN = COLORS.correct; // theme green for win pill
const OUTCOME_RED = COLORS.incorrect;

// Pill flash: alternates between theme color and lighter shade during reveal
const GREEN_LIGHT = '#5aad6a';   // lighter green for flash
const RED_LIGHT = '#d46e4e';     // lighter red-orange for flash
const CREAM_BRIGHT = '#fff8ee';  // brighter cream for text flash
const PILL_FLASH_ON = 140;       // ms at light color
const PILL_FLASH_OFF = 120;      // ms at normal color
const PILL_EXTRA_FLASHES = 2;    // flashes after prism settles

type SolveOutcomePhase = 'idle' | 'reveal-mount' | 'revealing' | 'done';

interface SolveButtonCellProps {
  onSolve: () => void;
  onClose?: () => void;
  solveActive: boolean;
  outcomeText: string | null;
  outcomeCorrect: boolean | null;
  onRevealComplete?: () => void;
  triggerReveal?: boolean;
  style?: React.CSSProperties;
}

const SolveButtonCell: React.FC<SolveButtonCellProps> = ({
  onSolve,
  onClose,
  solveActive,
  outcomeText,
  outcomeCorrect,
  onRevealComplete,
  triggerReveal,
  style,
}) => {
  const [isPressed, setIsPressed] = useState(false);
  const [outcomePhase, setOutcomePhase] = useState<SolveOutcomePhase>(
    () => (triggerReveal && outcomeText !== null) ? 'done' : 'idle'
  );
  const prevSolveActiveRef = useRef(false);

  // Pill flash state: true = light color, false = normal color
  const [pillFlashOn, setPillFlashOn] = useState(false);
  const pillFlashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pillFlashCount = useRef(0);
  const prismSettled = useRef(outcomePhase === 'done');

  // Start pill flashing when reveal-mount begins (prism about to become visible)
  const startPillFlash = useCallback(() => {
    pillFlashCount.current = 0;
    prismSettled.current = false;
    setPillFlashOn(true);
  }, []);

  // Stop flashing — clean up timer
  const stopPillFlash = useCallback(() => {
    if (pillFlashTimer.current) {
      clearTimeout(pillFlashTimer.current);
      pillFlashTimer.current = null;
    }
    setPillFlashOn(false);
  }, []);

  // Drive the flash cycle
  useEffect(() => {
    // Not flashing if phase is idle or done (done = settled + extra flashes finished)
    if (outcomePhase === 'idle' || outcomePhase === 'done') return;
    // Not started yet
    if (outcomePhase === 'reveal-mount') return;

    const schedule = () => {
      if (pillFlashOn) {
        // Currently light → go dark after ON duration
        pillFlashTimer.current = setTimeout(() => {
          setPillFlashOn(false);
          // If prism has settled, count this as a post-settle flash
          if (prismSettled.current) {
            pillFlashCount.current++;
          }
        }, PILL_FLASH_ON);
      } else {
        // Currently dark → check if we should flash again
        if (prismSettled.current && pillFlashCount.current >= PILL_EXTRA_FLASHES) {
          // All extra flashes done — stop
          return;
        }
        pillFlashTimer.current = setTimeout(() => {
          setPillFlashOn(true);
        }, PILL_FLASH_OFF);
      }
    };

    schedule();
    return () => {
      if (pillFlashTimer.current) {
        clearTimeout(pillFlashTimer.current);
        pillFlashTimer.current = null;
      }
    };
  }, [pillFlashOn, outcomePhase]);

  // Detect dialog open → close transition (back button — surround returns to cream)
  useEffect(() => {
    if (prevSolveActiveRef.current && !solveActive) {
      if (outcomeText === null) {
        setIsPressed(false);
      }
    }
    prevSolveActiveRef.current = solveActive;
  }, [solveActive, outcomeText]);

  // Start prism reveal when door halves are fully out of view
  useEffect(() => {
    if (triggerReveal && outcomePhase === 'idle') {
      setOutcomePhase('reveal-mount');
    }
  }, [triggerReveal, outcomePhase]);

  // reveal-mount → revealing via double-rAF (enables CSS transition)
  useEffect(() => {
    if (outcomePhase === 'reveal-mount') {
      let cancelled = false;
      startPillFlash();
      const timer = setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!cancelled) setOutcomePhase('revealing');
          });
        });
      }, SOLVE_OUTCOME_REVEAL_PAUSE);
      return () => { cancelled = true; clearTimeout(timer); };
    }
  }, [outcomePhase, startPillFlash]);

  const handleClick = () => {
    setIsPressed(true);
    onSolve();
  };

  const isInteractive = !isPressed && outcomePhase === 'idle';
  const prismAngle = (outcomePhase === 'revealing' || outcomePhase === 'done') ? 120 : 0;

  // Pill colors
  const isWin = outcomeCorrect === true;
  const pillBase = isWin ? OUTCOME_GREEN : OUTCOME_RED;
  const pillLight = isWin ? GREEN_LIGHT : RED_LIGHT;
  const pillColor = pillFlashOn && outcomePhase !== 'done' ? pillLight : pillBase;
  const textColor = pillFlashOn && outcomePhase !== 'done' ? CREAM_BRIGHT : CREAM;

  return (
    <PrismSlot
      geometry={SOLVE}
      angle={prismAngle}
      backingColor="#262626"
      transition={outcomePhase === 'revealing'
        ? `transform ${SOLVE_PRISM_DURATION}ms ease-in-out` : undefined}
      onTransitionEnd={() => {
        // Prism settled — allow extra flashes then transition to done
        prismSettled.current = true;
        // Schedule done after extra flashes complete
        const extraTime = PILL_EXTRA_FLASHES * (PILL_FLASH_ON + PILL_FLASH_OFF) + PILL_FLASH_ON + 100;
        setTimeout(() => {
          stopPillFlash();
          setOutcomePhase('done');
          onRevealComplete?.();
        }, extraTime);
      }}
      style={{ pointerEvents: (isInteractive || outcomePhase === 'done') ? 'auto' : 'none', ...style }}
    >
      {/* Face 0 (front): SolveButton over SolveButtonSurround on gold */}
      <PrismFace
        angle={0}
        data-debug-solve-btn="true"
        style={{ cursor: isInteractive ? 'pointer' : 'default', backgroundColor: '#d1930b' }}
        onClick={isInteractive ? handleClick : undefined}
      >
        {/* Surround — cream idle, highlight gold when pressed */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: isPressed ? HIGHLIGHT : CREAM,
          transition: isPressed ? 'color 100ms ease' : 'color 400ms ease',
        }}>
          <SvgSolveButtonSurround width={312} height={112} />
        </div>
        {/* Button — static, no animation */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}>
          <SvgSolveButton width={300} height={100} />
        </div>
      </PrismFace>

      {/* Face 1 (solution): paillette bg + solid outcome pill + flashing + text + close */}
      <PrismFace angle={-120} style={{ overflow: 'hidden', pointerEvents: 'auto' }}>
        {/* Layer 1: gold background frame around the solution pill */}
        <div style={{ position: 'absolute', inset: 0, backgroundColor: '#d1930b' }} />
        {/* Layer 2: solid outcome pill — green (win) / red-orange (lose), flashes lighter */}
        <div style={{
          position: 'absolute',
          top: 20,
          bottom: 20,
          left: 40,
          right: 100,
          borderRadius: 14,
          backgroundColor: pillColor,
          transition: 'background-color 60ms ease',
          zIndex: 1,
        }} />
        {/* Layer 3: answer text centered over pill */}
        <span style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 40,
          right: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'Inter, sans-serif',
          fontSize: 37,
          fontWeight: 800,
          color: textColor,
          transition: 'color 60ms ease',
          pointerEvents: 'none',
          userSelect: 'none',
          textAlign: 'center',
          zIndex: 2,
        }}>
          {outcomeText?.toUpperCase() ?? ''}
        </span>
        {/* Layer 4: close button — visible as soon as solution face rotates in */}
        {onClose && outcomeText !== null && (
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              right: 20,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 60,
              height: 60,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              zIndex: 3,
              padding: 0,
              outline: 'none',
            }}
            aria-label="Close"
          >
            <SvgBoardCloseButton width={60} height={60} />
          </button>
        )}
      </PrismFace>
    </PrismSlot>
  );
};

export default SolveButtonCell;
